import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const PonysSection = styled.section`
  padding: 5rem 0;
  background: ${props => props.theme.colors.gradients.card};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    font-family: ${props => props.theme.fonts.script};
    font-size: 3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.textLight};
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const PonysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PonyCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.card};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: ${props => props.theme.shadows.hover};
  }
`;

const PonyImage = styled.div`
  height: 200px;
  background: ${props => props.image ? `url(${props.image}) center/cover` : props.gradient || props.theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(transparent, rgba(0,0,0,0.2));
  }
`;

const PonyContent = styled.div`
  padding: 1rem;
  
  h3 {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 0;
    text-align: center;
    font-weight: 600;
  }
`;

const CallToAction = styled(motion.div)`
  text-align: center;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(46, 204, 113, 0.6);
  }
`;

const PonysPreview = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const featuredPonys = [
    {
      id: 'tweety',
      name: 'Tweety',
      image: '/assets/ponys/tweety.jpeg'
    },
    {
      id: 'rusty',
      name: 'Rusty',
      image: '/assets/ponys/rusty.jpeg'
    },
    {
      id: 'ronja',
      name: 'Ronja',
      image: '/assets/ponys/ronja.jpeg'
    }
  ];

  return (
    <PonysSection ref={ref}>
      <Container>
        <SectionHeader>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Unsere Ponys
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Lernen Sie unsere wundervollen vierbeinigen Lehrmeister kennen. 
            Jedes Pony hat seinen einzigartigen Charakter und besondere Fähigkeiten.
          </motion.p>
        </SectionHeader>

        <PonysGrid>
          {featuredPonys.map((pony, index) => (
            <PonyCard
              key={pony.id}
              initial={{ opacity: 0, y: 50, rotate: -5 }}
              animate={inView ? { opacity: 1, y: 0, rotate: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
            >
              <PonyImage image={pony.image}>
              </PonyImage>
              <PonyContent>
                <h3>{pony.name}</h3>
              </PonyContent>
            </PonyCard>
          ))}
        </PonysGrid>

        <CallToAction
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h3>Möchten Sie alle unsere Ponys kennenlernen?</h3>
          <p>
            Jedes unserer Ponys hat eine besondere Persönlichkeit und eigene Stärken. 
            Entdecken Sie, welches Pony am besten zu Ihnen oder Ihrem Kind passt.
          </p>
          <CTAButton to="/ponys">
            Alle Ponys entdecken
          </CTAButton>
        </CallToAction>
      </Container>
    </PonysSection>
  );
};

export default PonysPreview;